import { useEffect, useRef } from 'react';

/**
 * Single-letter shortcuts on the modal's first step. Ignored while a field
 * has focus, with a modifier held, or mid IME composition, and never wired
 * on touch-first or small screens (`enabled` false). No global binding uses
 * these letters (`useCanvasShortcuts` and the editor bind only modified keys).
 */
export function useCreateShortcuts(
  enabled: boolean,
  shortcuts: ReadonlyArray<{ readonly key: string; readonly run: () => void }>,
) {
  // Read through a ref: the list is rebuilt every render, and re-binding the
  // window listener on each one is wasted work.
  const latest = useRef(shortcuts);
  latest.current = shortcuts;
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.isComposing) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const match = latest.current.find((s) => s.key.toLowerCase() === event.key.toLowerCase());
      if (!match) return;
      event.preventDefault();
      match.run();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
